import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/apiClient";
import {
  Activity, Users, UserCircle2, Sparkles, Gavel, Star, Gift, TrendingUp,
  ChevronRight, Leaf, Bookmark, Package, Clock, Flame,
} from "lucide-react";

/**
 * v3.66 Platform Pulse — home-page visual stats section. Renders the same
 * for logged-in and logged-out visitors (this is intentional — the data is
 * platform health, not personal). Mixes large counters with a 14-day
 * sparkline and a top-5 breeder leaderboard. Compact, dark-mode-friendly,
 * no external chart lib.
 */
export default function PlatformPulse() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/public/stats").then(r => setData(r.data)).catch(() => setData(null));
  }, []);

  if (!data) {
    return (
      <section className="bg-[var(--bg)] border-y border-[var(--border)] py-12"
               data-testid="platform-pulse-loading">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="h-6 w-40 bg-[var(--surface-2)] mb-6"/>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[0,1,2,3].map(i => <div key={i} className="bg-[var(--surface)] border border-[var(--border)] h-28"/>)}
          </div>
        </div>
      </section>
    );
  }

  const { counts, series, top_breeders, auctions_visible, latest_listing, most_watched_drop } = data;
  // v3.99c.2 — sparkline-aligned label so "+N in last 14d" matches the chart total.
  const newLast14d = counts.new_listings_14d_all ?? counts.new_drops_14d ?? 0;

  return (
    <section className="bg-[var(--bg)] py-14 md:py-16" data-testid="platform-pulse">
      <div className="max-w-[1400px] mx-auto px-6">
        <header className="flex items-end justify-between flex-wrap gap-3 mb-6">
          <div>
            <div className="label-eyebrow mb-2 flex items-center gap-2" style={{color:"var(--gold)"}}>
              <Activity className="w-3 h-3"/> Platform Pulse
            </div>
            <h2 className="font-display text-3xl md:text-5xl tracking-tighter" style={{color:"var(--text)"}}>
              The state of <span style={{color:"var(--primary-text)"}}>CannaForge</span>
            </h2>
          </div>
          <span className="text-[11px] font-mono" style={{color:"var(--text-muted)"}}>
            updated {new Date(data.generated_at).toLocaleTimeString([], {hour:"2-digit", minute:"2-digit"})}
          </span>
        </header>

        {/* Big counters row */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3 md:gap-4 mb-5">
          <BigCounter icon={Users} label="Verified breeders" value={counts.breeders} accent="var(--gold)"
                      sub={counts.new_breeders_30d > 0
                        ? `+${counts.new_breeders_30d} new in last 30d`
                        : "hand-onboarded"}
                      testid="pulse-breeders"
                      href="/breeders"/>
          {/* v3.99h — Collectors counter sits next to breeders so the
              "community size" story reads in one glance. */}
          <BigCounter icon={UserCircle2} label="Collectors" value={counts.collectors || 0} accent="#7CC9FF"
                      sub="and growing" testid="pulse-collectors"
                      href="/join-collector"/>
          <BigCounter icon={Sparkles} label="Genetics live" value={counts.listings_live} accent="var(--primary-text)"
                      sub={`${newLast14d} added in last 14d`} testid="pulse-listings"
                      href="/shop"/>
          <BigCounter icon={Leaf} label="Lineage records" value={counts.lineage_total || 0} accent="#A7E0C0"
                      sub={`${counts.lineage_verified || 0} verified`} testid="pulse-lineage"
                      href="/lineage"/>
          <BigCounter
            icon={Bookmark}
            label="Drop watchers"
            value={counts.drop_watchers_total || 0}
            accent="#FFB07A"
            sub={(counts.upcoming_drops || 0) > 0
              ? `waiting on ${counts.upcoming_drops} upcoming drop${counts.upcoming_drops === 1 ? "" : "s"}`
              : "waiting on the next release"}
            testid="pulse-drop-watchers"
            href="/new-drops"
          />
          {/* v3.99c.2 — surfaces commerce activity. Hidden if zero so we
              don't telegraph "no sales yet" pre-launch. */}
          {(counts.units_sold || 0) > 0 && (
            <BigCounter
              icon={Package}
              label="Packs shipped"
              value={counts.units_sold || 0}
              accent="#F4D45F"
              sub="and counting"
              testid="pulse-units-sold"
              href="/shop"
            />
          )}
          {auctions_visible && (
            <BigCounter icon={Gavel} label="Active auctions" value={counts.active_auctions} accent="#8FB3FF"
                        sub="ending soon" testid="pulse-auctions"
                        href="/auctions"/>
          )}
          <BigCounter icon={Star} label="Reviews" value={counts.approved_reviews} accent="var(--secondary)"
                      sub="verified & approved" testid="pulse-reviews"
                      href="/reviews"/>
          <BigCounter
            icon={Gift}
            label="Live giveaways"
            value={counts.active_giveaways || 0}
            accent="#C28FFF"
            testid="pulse-giveaways"
            href="/giveaways#live"
            subLinks={[
              { count: counts.upcoming_giveaways || 0, label: "upcoming", href: "/giveaways#upcoming" },
              { count: counts.ended_giveaways || 0,    label: "ended",    href: "/giveaways#ended"    },
            ]}
            subFallback="next one coming"
          />
          {(counts.total_followers || 0) > 0 && (
            <BigCounter icon={TrendingUp} label="Breeder followers" value={counts.total_followers || 0} accent="#FF8FB1"
                        sub="across the index" testid="pulse-followers"
                        href="/breeders"/>
          )}
        </div>

        {/* v3.99c.2 — Just-landed + Most-watched-drop strip. Renders only when
            data exists so the home page stays tight on a fresh install. */}
        {(latest_listing || most_watched_drop) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 mb-5">
            {latest_listing && <JustLandedCard listing={latest_listing}/>}
            {most_watched_drop && <MostWatchedDropCard drop={most_watched_drop}/>}
          </div>
        )}

        {/* 14-day sparklines + leaderboard side by side */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <SparklineCard
            title="New genetics — 14 days"
            sub="Drops onboarded daily"
            values={series.listings_per_day}
            days={series.days}
            accent="var(--primary-text)"
            icon={Sparkles}
            testid="pulse-spark-listings"
            href="/shop"
          />
          <SparklineCard
            title="Reviews — 14 days"
            sub="Approved collector reviews"
            values={series.reviews_per_day}
            days={series.days}
            accent="var(--gold)"
            icon={Star}
            testid="pulse-spark-reviews"
            href="/reviews"
          />
          <LeaderboardCard breeders={top_breeders} giveaways_count={counts.active_giveaways}/>
        </div>

        <div className="text-[11px] font-mono mt-5" style={{color:"var(--text-muted)"}}>
          Platform Pulse data refreshes on each page load · {counts.breeders} breeders · {counts.collectors || 0} collectors · {counts.listings_live} live genetics ·{" "}
          {counts.approved_reviews} approved reviews
          {(counts.units_sold || 0) > 0 && <> · {counts.units_sold} packs shipped</>}
        </div>
      </div>
    </section>
  );
}

function BigCounter({ icon: Icon, label, value, sub, accent, testid, href, subLinks, subFallback }) {
  // v3.99c.28 — "Everything clickable" pass. When `subLinks` is provided
  // (array of {count, label, href}), the sub-text segments render as
  // individual clickable pills BELOW the main tile link instead of as a
  // dead inline string. Avoids nested-anchor invalid HTML by keeping the
  // main click area (icon + label + count) as one Link, then placing the
  // sub-link strip outside the Link as siblings inside the same card div.
  const head = (
    <>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4" style={{color:accent}}/>
        <div className="label-eyebrow" style={{color:"var(--text-muted)"}}>{label}</div>
      </div>
      <div className="font-mono text-4xl md:text-5xl tracking-tighter leading-none" style={{color:accent}}>
        {(value ?? 0).toLocaleString()}
      </div>
    </>
  );
  const cardCls = "bg-[var(--surface)] border border-[var(--border)] hover:border-[color:var(--gold)]/60 transition-colors p-5 md:p-6 flex flex-col gap-2";
  const style = { borderLeftWidth: 3, borderLeftColor: accent };

  // Render sub-link strip if any segment has a non-zero count.
  let subBlock = null;
  if (subLinks && subLinks.some(s => (s.count || 0) > 0)) {
    subBlock = (
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] font-mono mt-1"
           style={{color:"var(--text-muted)"}}>
        {subLinks.filter(s => (s.count || 0) > 0).map((s, i, arr) => (
          <React.Fragment key={s.label}>
            <Link to={s.href}
                  className="hover:text-[var(--gold)] hover:underline transition-colors"
                  data-testid={`${testid}-sub-${s.label}`}>
              <span style={{color:"var(--text-2)"}}>{s.count}</span> {s.label}
            </Link>
            {i < arr.length - 1 && <span aria-hidden="true">·</span>}
          </React.Fragment>
        ))}
      </div>
    );
  } else if (subLinks && subFallback) {
    subBlock = <div className="text-[11px] font-mono mt-1" style={{color:"var(--text-muted)"}}>{subFallback}</div>;
  } else if (sub) {
    subBlock = <div className="text-[11px] font-mono mt-1" style={{color:"var(--text-muted)"}}>{sub}</div>;
  }

  if (href) {
    // Card-level link covers icon/label/value. The sub-link strip sits
    // outside the <Link> so its inner <Link>s don't create nested
    // anchors. Visually the whole card still feels clickable thanks to
    // the matching hover border + cursor.
    return (
      <div className={cardCls} style={style} data-testid={testid}>
        <Link to={href} className="block" data-testid={`${testid}-main`}>
          {head}
        </Link>
        {subBlock}
      </div>
    );
  }
  return (
    <div className={cardCls} style={style} data-testid={testid}>
      {head}
      {subBlock}
    </div>
  );
}

function SparklineCard({ title, sub, values, days, accent, icon: Icon, testid, href }) {
  const max = Math.max(1, ...values);
  const total = values.reduce((s, n) => s + n, 0);
  const w = 280, h = 64, step = w / Math.max(1, values.length - 1);
  const points = values.map((v, i) => `${(i * step).toFixed(1)},${(h - (v / max) * (h - 6) - 3).toFixed(1)}`).join(" ");
  const area = `0,${h} ${points} ${w},${h}`;
  const inner = (
    <>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4" style={{color:accent}}/>
          <div className="label-eyebrow" style={{color:"var(--text-muted)"}}>{title}</div>
        </div>
        <div className="font-mono text-xs" style={{color:accent}}>{total} this fortnight</div>
      </div>
      <div className="font-mono text-3xl tracking-tighter leading-none mb-3" style={{color:"var(--text)"}}>
        {total}
      </div>
      <svg viewBox={`0 0 ${w} ${h}`} width="100%" height={h} preserveAspectRatio="none"
           role="img" aria-label={`${title}: ${total} total`}>
        <defs>
          <linearGradient id={`grad-${title.replace(/\s+/g,"-")}`} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={accent} stopOpacity="0.35"/>
            <stop offset="100%" stopColor={accent} stopOpacity="0"/>
          </linearGradient>
        </defs>
        <polygon points={area} fill={`url(#grad-${title.replace(/\s+/g,"-")})`}/>
        <polyline points={points} fill="none" stroke={accent} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round"/>
        {values.map((v, i) => v > 0 ? (
          <circle key={i} cx={(i * step).toFixed(1)} cy={(h - (v / max) * (h - 6) - 3).toFixed(1)}
                  r="2" fill={accent}/>
        ) : null)}
      </svg>
      <div className="text-[10px] font-mono mt-2 flex justify-between" style={{color:"var(--text-muted)"}}>
        <span>{days[0]}</span>
        <span>{sub}</span>
        <span>{days[days.length - 1]}</span>
      </div>
    </>
  );
  const cls = "bg-[var(--surface)] border border-[var(--border)] hover:border-[color:var(--gold)]/60 transition-colors p-5 md:p-6 block";
  if (href) {
    return <Link to={href} className={cls} data-testid={testid}>{inner}</Link>;
  }
  return <div className={cls} data-testid={testid}>{inner}</div>;
}

function LeaderboardCard({ breeders, giveaways_count }) {  if (!breeders || breeders.length === 0) {
    return (
      <div className="bg-[var(--surface)] border border-[var(--border)] p-5" data-testid="pulse-leaderboard-empty">
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp className="w-4 h-4" style={{color:"var(--gold)"}}/>
          <div className="label-eyebrow" style={{color:"var(--text-muted)"}}>Top breeders</div>
        </div>
        <div className="text-sm py-6 text-center" style={{color:"var(--text-muted)"}}>
          Leaderboard fills as breeders earn approved reviews.
        </div>
        {giveaways_count > 0 && (
          <Link to="/giveaways" className="block text-xs mt-2 underline" style={{color:"var(--gold)"}}>
            {giveaways_count} live giveaway{giveaways_count === 1 ? "" : "s"} →
          </Link>
        )}
      </div>
    );
  }
  return (
    <div className="bg-[var(--surface)] border border-[var(--border)] p-5" data-testid="pulse-leaderboard">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4" style={{color:"var(--gold)"}}/>
          <div className="label-eyebrow" style={{color:"var(--text-muted)"}}>Top breeders</div>
        </div>
        <Link to="/breeders" className="text-[11px] underline" style={{color:"var(--gold)"}}>all →</Link>
      </div>
      <div className="space-y-2">
        {breeders.map((v, i) => (
          <Link key={v.id} to={`/breeder/${v.store_slug || v.id}`}
                className="flex items-center gap-3 p-1.5 hover:bg-[var(--surface-2)] transition-colors group"
                data-testid={`pulse-leader-${v.id}`}>
            <span className="font-mono text-sm w-5 text-right" style={{color: i === 0 ? "var(--gold)" : "var(--text-muted)"}}>
              {i + 1}
            </span>
            {v.logo_url ? (
              <img src={v.logo_url} alt="" className="w-8 h-8 object-cover border border-[var(--border)] shrink-0"/>
            ) : (
              <div className="w-8 h-8 bg-[var(--surface-2)] border border-[var(--border)] shrink-0"/>
            )}
            <div className="min-w-0 flex-1">
              <div className="text-sm truncate group-hover:text-[var(--primary-text)] transition-colors"
                   style={{color:"var(--text)"}}>{v.store_name}</div>
              <div className="text-[10px] font-mono" style={{color:"var(--text-muted)"}}>
                ★ {(v.rating || 0).toFixed(1)} · {v.review_count || 0} review{(v.review_count || 0) === 1 ? "" : "s"}
                <> · {v.follower_count || 0} follower{(v.follower_count || 0) === 1 ? "" : "s"}</>
              </div>
            </div>
            <ChevronRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity"
                          style={{color:"var(--text-muted)"}}/>
          </Link>
        ))}
      </div>
    </div>
  );
}


function JustLandedCard({ listing }) {
  if (!listing) return null;
  const href = listing.slug ? `/strain/${listing.slug}` : "/shop";
  const when = listing.created_at ? new Date(listing.created_at) : null;
  const ago = when ? friendlyAgo(when) : null;
  return (
    <Link
      to={href}
      className="bg-[var(--surface)] border border-[var(--border)] hover:border-[color:var(--primary-text)]/60 transition-colors p-5 md:p-6 flex items-start gap-4 group"
      style={{ borderLeftWidth: 3, borderLeftColor: "var(--primary-text)" }}
      data-testid="pulse-just-landed"
    >
      <div className="w-10 h-10 flex items-center justify-center bg-[var(--surface-2)] border border-[var(--border-strong)] shrink-0">
        <Sparkles className="w-5 h-5" style={{ color: "var(--primary-text)" }} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="label-eyebrow mb-1" style={{ color: "var(--text-muted)" }}>Just landed</div>
        <div className="font-display text-lg md:text-xl tracking-tight truncate group-hover:text-[var(--primary-text)] transition-colors" style={{ color: "var(--text)" }}>
          {listing.title || "—"}
        </div>
        <div className="mt-1 text-[11px] font-mono" style={{ color: "var(--text-muted)" }}>
          {listing.breeder ? <>by <span style={{color:"var(--text-2)"}}>{listing.breeder}</span></> : "newly listed"}
          {ago && <> · {ago}</>}
        </div>
      </div>
      <ChevronRight className="w-4 h-4 mt-1 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: "var(--text-muted)" }} />
    </Link>
  );
}

function MostWatchedDropCard({ drop }) {
  if (!drop) return null;
  const href = drop.slug ? `/drops/${drop.slug}` : "/new-drops";
  const when = drop.scheduled_at ? new Date(drop.scheduled_at) : null;
  const inFuture = when && when.getTime() > Date.now();
  const eta = when ? (inFuture ? `drops ${when.toLocaleDateString([], { month: "short", day: "numeric" })}` : "live now") : null;
  return (
    <Link
      to={href}
      className="bg-[var(--surface)] border border-[var(--border)] hover:border-[color:#FFB07A]/60 transition-colors p-5 md:p-6 flex items-start gap-4 group"
      style={{ borderLeftWidth: 3, borderLeftColor: "#FFB07A" }}
      data-testid="pulse-most-watched-drop"
    >
      <div className="w-10 h-10 flex items-center justify-center bg-[var(--surface-2)] border border-[var(--border-strong)] shrink-0">
        <Flame className="w-5 h-5" style={{ color: "#FFB07A" }} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="label-eyebrow mb-1 flex items-center gap-1.5" style={{ color: "var(--text-muted)" }}>
          Most-watched drop
          {drop.watchers > 0 && (
            <span className="font-mono" style={{ color: "#FFB07A" }}>
              · {drop.watchers} watching
            </span>
          )}
        </div>
        <div className="font-display text-lg md:text-xl tracking-tight truncate group-hover:text-[#FFB07A] transition-colors" style={{ color: "var(--text)" }}>
          {drop.name || "—"}
        </div>
        <div className="mt-1 text-[11px] font-mono flex items-center gap-2" style={{ color: "var(--text-muted)" }}>
          {drop.breeder && <>by <span style={{color:"var(--text-2)"}}>{drop.breeder}</span></>}
          {eta && <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {eta}</span>}
        </div>
      </div>
      <ChevronRight className="w-4 h-4 mt-1 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: "var(--text-muted)" }} />
    </Link>
  );
}

function friendlyAgo(when) {
  const sec = (Date.now() - when.getTime()) / 1000;
  if (sec < 60) return "just now";
  if (sec < 3600) return `${Math.round(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.round(sec / 3600)}h ago`;
  if (sec < 604800) return `${Math.round(sec / 86400)}d ago`;
  return when.toLocaleDateString([], { month: "short", day: "numeric" });
}
